/**
 * Localized FB funnel helper.
 *
 * The paid-ad funnel lives at /fb/onboarding (English) and at /fb/:lang/onboarding
 * for localized variants. This hook reads the optional :lang segment, forces the
 * app UI language on mount so the whole funnel (onboarding -> pricing -> success)
 * stays in that language, and tags PostHog with a funnel_lang super-property so
 * every downstream event is attributable to the localized funnel.
 *
 * It also returns fbPath(), a builder that keeps the /fb/<lang> prefix on
 * intra-funnel navigation so a Spanish visitor never gets bounced to the English
 * funnel between steps.
 */
import { useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { useLanguage } from '@/contexts/LanguageContext';

// Languages with an end-to-end localized funnel: onboarding copy in
// FbOnboarding/locales/<lang>.json AND paywall copy in FbPricing/copy.js.
// de and no have the onboarding half only, so a German ad would hand a German
// parent an English paywall. They join this set the day copy.js gets them.
const FB_FUNNEL_LANGS = new Set(['es', 'fr', 'lt', 'ru']);

// Country-shaped aliases that all mean "run this funnel in English", so an ad
// set can point at a URL that names its own audience (/fb/uk/onboarding) and
// read sensibly in the ads manager.
//
// Note 'uk' here means the United Kingdom, not Ukrainian. ISO 639-1 'uk' IS
// Ukrainian, so if Ukrainian is ever added to the funnel it needs a different
// segment (say 'ua') or this alias has to go.
const ENGLISH_ALIASES = new Set(['en', 'uk', 'gb', 'us', 'au', 'ie', 'ca', 'nz']);

export function useFbFunnelLang() {
  const params = useParams();
  const { pathname } = useLocation();
  const { language, setLanguage } = useLanguage();

  const seg = params.lang || null;
  const lang = seg && FB_FUNNEL_LANGS.has(seg) ? seg : null;
  const isEnglishAlias = seg ? ENGLISH_ALIASES.has(seg) : false;

  // Paid traffic must be deterministic. A cold ad click on /fb/onboarding has to
  // render English every time, whatever happens to be sitting in this browser's
  // stored language: the ad creative was written in one language and the landing
  // funnel has to match it. Following the stored language here meant anyone who
  // had ever viewed the app in Spanish got a Spanish funnel from an English ad.
  //
  // The organic /onboarding route is the opposite case. Those visitors came from
  // a landing page they just read in their own language, so their choice is real
  // information and is left alone.
  const isAdsFunnel = pathname.startsWith('/fb');
  const forcedLang = lang || (isEnglishAlias || isAdsFunnel ? 'en' : null);

  const appLang = FB_FUNNEL_LANGS.has(language) ? language : 'en';

  useEffect(() => {
    if (!forcedLang) return;
    setLanguage(forcedLang);
    try {
      if (typeof posthog.register === 'function') {
        posthog.register({ funnel_lang: forcedLang });
      }
    } catch (_) { /* posthog not ready / opted out: non-fatal */ }
  }, [forcedLang, setLanguage]);

  // Base path for this funnel: '/fb/es', '/fb/uk', or '/fb'. An English alias
  // keeps its segment so intra-funnel navigation stays on the URL the ad used
  // and the whole journey reports under one path.
  const keptSeg = lang || (isEnglishAlias ? seg : null);
  const base = keptSeg ? `/fb/${keptSeg}` : '/fb';

  return useMemo(() => ({
    lang,               // the :lang segment if it is a real funnel language, else null
    funnelLang: forcedLang || appLang,
    base,
    // fbPath('/pricing') -> '/fb/es/pricing' (or '/fb/pricing' for English)
    fbPath: (sub) => `${base}${sub.startsWith('/') ? sub : `/${sub}`}`,
  }), [lang, forcedLang, appLang, base]);
}
